import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Box, Clock, AlertTriangle } from 'lucide-react';
import { AcmDoor3DViewer } from '../components/acm/AcmDoor3DViewer';
import { AcmDoor3DParams } from '../lib/acm3d';

type Status = 'loading' | 'ready' | 'expired' | 'not_found';

export function AcmDoorPublicViewer() {
    const { token } = useParams();
    const [status, setStatus] = useState<Status>('loading');
    const [params, setParams] = useState<AcmDoor3DParams | null>(null);
    const [projeto, setProjeto] = useState<string>('');

    useEffect(() => {
        const fetchLink = async () => {
            if (!token || !supabase) {
                setStatus('not_found');
                return;
            }
            try {
                const { data, error } = await supabase
                    .from('acm_door_public_links')
                    .select('params, expires_at')
                    .eq('id', token)
                    .single();

                if (error || !data) {
                    setStatus('not_found');
                    return;
                }
                if (new Date(data.expires_at).getTime() <= Date.now()) {
                    setStatus('expired');
                    return;
                }
                setParams(data.params as AcmDoor3DParams);
                setProjeto((data.params as AcmDoor3DParams).projeto || 'Porta em ACM');
                setStatus('ready');
            } catch (err) {
                console.error('Erro ao carregar link público da porta 3D', err);
                setStatus('not_found');
            }
        };

        fetchLink();
    }, [token]);

    if (status === 'loading') {
        return (
            <div className="min-h-screen bg-slate-100 dark:bg-slate-950 flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
            </div>
        );
    }

    if (status === 'expired') {
        return (
            <div className="min-h-screen bg-slate-100 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex items-center justify-center p-6">
                <div className="text-center max-w-sm">
                    <Clock className="w-10 h-10 mx-auto mb-4 text-blue-600 dark:text-blue-400" />
                    <h1 className="text-lg font-bold mb-2">Link expirado</h1>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        Este link de visualização 3D era válido por 30 minutos e já expirou.
                        Peça um novo link a quem compartilhou este projeto com você.
                    </p>
                </div>
            </div>
        );
    }

    if (status === 'not_found' || !params) {
        return (
            <div className="min-h-screen bg-slate-100 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex items-center justify-center p-6">
                <div className="text-center max-w-sm">
                    <AlertTriangle className="w-10 h-10 mx-auto mb-4 text-blue-600 dark:text-blue-400" />
                    <h1 className="text-lg font-bold mb-2">Link não encontrado</h1>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        Este link não existe ou já foi removido. Peça um novo link a quem
                        compartilhou este projeto com você.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-[100dvh] bg-slate-100 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex flex-col overflow-hidden overscroll-none">
            <div className="flex items-center gap-3 p-3 sm:p-4 border-b border-slate-200 dark:border-slate-800 shrink-0">
                <div className="p-2 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded-lg shrink-0">
                    <Box className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                    <h1 className="text-sm font-bold truncate">{projeto}</h1>
                    <p className="text-[11px] text-slate-600 dark:text-slate-400">Visualização 3D · link temporário</p>
                </div>
            </div>
            <div className="flex-1 min-h-0">
                <AcmDoor3DViewer params={params} />
            </div>
        </div>
    );
}
